import React, { useEffect, useState } from 'react';
import DefaultToolBar from '../../../components/default/DefaultToolBar';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import DefaultModal from '../../../components/default/DefaultModal';
import { toast } from 'react-toastify';
import {
    WarehouseOrderItemFilters,
    WarehouseOrderItemDataForm
} from '../../../app/models/WarehouseOrderItem';
import useWarehouseOrderItemService from '../../../app/services/hooks/useWarehouseOrderItemService';
import WarehouseOrderItemCreate from './WarehouseOrderItemCreate';
import WarehouseOrderItemEdit from './WarehouseOrderItemEdit';
import useSweetAlert from '../../../hooks/useSweetAlert';
import WarehouseOrderItemShow from './WarehouseOrderItemShow';
import LazyLoading from '../../../components/LazyLoading';
import { WarehouseOrderItemStatus } from '../../../app/models/WarehouseOrderItemStatus';
import { WarehouseOrderDataForm } from '../../../app/models/WarehouseOrder';
import WarehouseQuoteItemsDataTable from './tables/WarehouseOrderItemDataTable';
import WarehouseOrderItemsDataTable from './tables/WarehouseOrderItemDataTable';

interface Props {
    warehouseOrderId: number;
    orderId?: number;
    canBeEdited: boolean;
    isShow?: boolean;
    currencyId: number | string;
    warehouseOrderForm: WarehouseOrderDataForm;
    setWarehouseOrderForm?: (warehouseOrderForm: WarehouseOrderDataForm) => void;
    reloadModule?: () => void;
}

const WarehouseOrderItemsContainer = ({
    warehouseOrderId,
    orderId,
    canBeEdited,
    currencyId,
    warehouseOrderForm,
    setWarehouseOrderForm,
    reloadModule,
    isShow = false
}: Props) => {
    const {
        fetchingGetWarehouseOrderItemsByOrder,
        getAllWarehouseOrderItemsByOrder,
        getAllWarehouseOrderItemsByUnifiedOrder,
        deleteWarehouseOrderItem
    } = useWarehouseOrderItemService();

    const [showingEdit, setShowingEdit] = useState(false);
    const [showingShow, setShowingShow] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [showingSplit, setShowingSplit] = useState(false);
    const [showingEditRates, setShowingEditRates] = useState(false);
    const [statuses, setStatuses] = useState<WarehouseOrderItemStatus[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [warehouseOrderItemIdEdit, setWarehouseOrderItemIdEdit] = useState<number>(-1);
    const [warehouseOrderItemIdEditRates, setWarehouseOrderItemIdEditRates] =
        useState<number>(-1);
    const [warehouseOrderItemIdShow, setWarehouseOrderItemIdShow] = useState<number>(-1);
    const [warehouseOrderItemIdSplit, setWarehouseOrderItemIdSplit] =
        useState<number>(-1);

    const [warehouseOrderItems, setWarehouseOrderItems] = useState<
        WarehouseOrderItemDataForm[]
    >([]);

    const [filter, setFilter] = useState<WarehouseOrderItemFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        if (warehouseOrderId) {
            reloadTable();
        }
    }, [warehouseOrderId]);

    useEffect(() => {
        if (orderId) {
            reloadTable();
        }
    }, [orderId]);

    useEffect(() => {
        reloadTable();
    }, [filter]);

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (warehouseOrderItemId: number) => {
        setShowingEdit(true);
        setWarehouseOrderItemIdEdit(warehouseOrderItemId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setWarehouseOrderItemIdEdit(-1);
    };

    const showShow = (warehouseOrderItemId: number) => {
        setShowingShow(true);
        setWarehouseOrderItemIdShow(warehouseOrderItemId);
    };

    const showEditRates = (warehouseOrderItemId: number) => {
        setShowingEditRates(true);
        setWarehouseOrderItemIdEditRates(warehouseOrderItemId);
    };

    const hideEditRates = () => {
        setShowingEditRates(false);
        setWarehouseOrderItemIdEditRates(-1);
    };

    const hideShow = () => {
        setShowingShow(false);
        setWarehouseOrderItemIdShow(-1);
    };

    const showSplit = (warehouseOrderItemId: number) => {
        setShowingSplit(true);
        setWarehouseOrderItemIdSplit(warehouseOrderItemId);
    };

    const hideSplit = () => {
        setShowingSplit(false);
        setWarehouseOrderItemIdSplit(-1);
    };

    const destroy = (warehouseOrderItemId: number) => {
        const _text =
            'Está a punto de eliminar el item de pedido de almacenamiento #' +
            warehouseOrderItemId;

        useSweetAlert().requestConfirmation({
            title: '¿Está seguro?',
            text: _text,
            confirmButtonText: 'Si, eliminar',
            cancelButtonText: 'Cancelar',
            onConfirm: () => {
                useSweetAlert().spinnerAlert('Eliminando detalle pedido...', 400);
                deleteWarehouseOrderItem(warehouseOrderItemId, {
                    onSuccess: (response: ServiceResponse) => {
                        useSweetAlert().successAlert({
                            title: 'Éxito',
                            text: response.message
                        });
                        reloadTable();
                        if (reloadModule) reloadModule();
                    },
                    onError: (response: ServiceResponse) => {
                        useSweetAlert().errorAlert({
                            title: 'Error',
                            text: response.message
                        });
                    }
                });
            },
            onCancel: () => {
                useSweetAlert().infoAlert({
                    title: 'Cancelado',
                    text: 'El item de pedido de almacenamiento no se ha eliminado.'
                });
            }
        });
    };

    const reloadTable = () => {
        if (orderId) {
            getAllWarehouseOrderItemsByUnifiedOrder(filter, orderId, {
                onSuccess: (response: ServiceResponse) => {
                    setWarehouseOrderItems(response.data.warehouse_order_items);
                    setTotalRows(response.data.total_rows);
                    setStatuses(response.data.statuses ?? []);
                }
            });
        } else {
            getAllWarehouseOrderItemsByOrder(filter, warehouseOrderId, {
                onSuccess: (response: ServiceResponse) => {
                    setWarehouseOrderItems(response.data.warehouse_order_items);
                    setTotalRows(response.data.total_rows);
                    setStatuses(response.data.statuses ?? []);
                }
            });
        }
    };

    return (
        <>
            <DefaultCard>
                <div className="row">
                    <div className="col-12">
                        {!isShow ? (
                            <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} />} />
                        ) : null}
                    </div>
                    <div className="col-12">
                        {isShow ? (
                            <>
                                <WarehouseOrderItemsDataTable
                                    warehouseOrderItems={warehouseOrderItems}
                                    totalRows={totalRows}
                                    statuses={statuses}
                                    loading={fetchingGetWarehouseOrderItemsByOrder}
                                    filter={filter}
                                    setFilter={setFilter}
                                    show={showShow}
                                    changeStatus={false}
                                    canBeEdited={!isShow}
                                />
                            </>
                        ) : (
                            <>
                                <WarehouseOrderItemsDataTable
                                    warehouseOrderItems={warehouseOrderItems}
                                    totalRows={totalRows}
                                    statuses={statuses}
                                    loading={fetchingGetWarehouseOrderItemsByOrder}
                                    destroy={destroy}
                                    edit={showEdit}
                                    filter={filter}
                                    setFilter={setFilter}
                                    show={showShow}
                                    changeStatus={false}
                                    canBeEdited={!isShow}
                                    reloadModule={reloadModule}
                                    callbackSuccess={reloadTable}
                                    // reloadModule={reloadModule}
                                    // callbackSuccess={reloadTable}
                                />
                            </>
                        )}
                    </div>
                </div>
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Detalle Pedido"
                    backdrop={true}
                    showFooter={false}
                >
                    <WarehouseOrderItemCreate
                        canBeEdited={canBeEdited}
                        warehouseOrderId={warehouseOrderId}
                        orderId={orderId}
                        onSaved={() => {
                            reloadTable();
                            if (reloadModule) reloadModule();
                            hideCreate();
                        }}
                        onError={() => {
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        currencyId={currencyId}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Detalle Pedido"
                    backdrop={true}
                    showFooter={false}
                >
                    <WarehouseOrderItemEdit
                        canBeEdited={canBeEdited}
                        warehouseOrderItemId={warehouseOrderItemIdEdit}
                        warehouseOrderId={warehouseOrderId}
                        onSaved={() => {
                            reloadTable();
                            if (reloadModule) reloadModule();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={() => {
                            hideEdit();
                        }}
                    />
                </DefaultModal>
            ) : null}
            {showingShow ? (
                <DefaultModal
                    show={showingShow}
                    handleClose={hideShow}
                    title="Ver detalle del item"
                    backdrop={true}
                    showFooter={false}
                >
                    <WarehouseOrderItemShow
                        warehouseOrderItemId={warehouseOrderItemIdShow}
                        onError={(message?: string) => {
                            hideShow();
                            toast.error(message ?? 'Error al obtener el item de transporte');
                        }}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default WarehouseOrderItemsContainer;
